import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { Order, OrderItem } from "../../lib/types.ts";
import type { Aktion } from "../../state/reduzierer.ts";
import { lagerKennung, type JtlLager } from "../../lib/lager.ts";
import { anfrage, ApiFehler } from "../../api/basis.ts";
import {
  bestellungAendern,
  bestellungAnlegen,
  NachladenNachSchreibenFehler,
  SSE_ENTITAET as BESTELLUNG_ENTITAET,
} from "../../api/bestellungen.ts";
import { dokumentHochladen } from "../../api/dokumente.ts";
import { positionszeilenAnlegen, positionszeilenAus } from "../../api/positionen.ts";
import { jtlBestellungLaden } from "../../api/jtlBestellungen.ts";
import { ausgangUnklar, URSACHEN_MIT_WIEDERHOLUNGSRAT } from "../../lib/ausgangUnklar.ts";
import { fehlertext, NutzerFehler } from "../../lib/fehlertext.ts";
import { dateiZuGrossText, istZuGross, uploadFehlerText } from "../../lib/upload.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import type { AnlagemaskeDaten } from "../NewOrder/Anlagemaske.tsx";

/**
 * Eine Position, wie `POST /api/jtl/orders` sie annimmt (`backend/src/jtl/schreiben/positionenXml.ts`
 * `JtlNeuePosition`, geprueft von `alsJtlNeuePosition` in `jtlSchreiben.ts`). Bewusst hier und nicht
 * aus dem Backend importiert - Frontend und Backend teilen keinen Typraum; diese Form ist Teil der
 * HTTP-Schnittstelle und wird von `verdrahtungAnlage.test.tsx` gegen den echten Koerper geprueft.
 */
interface JtlNeuePosition {
  kArtikel: number;
  cArtNr: string;
  fMenge: number;
  fEKNetto: number;
  fMwSt: number;
  dLieferdatum: string | null;
}

/** Anzeigetext, solange JTL die Bestellnummer noch nicht vergeben hat - wortgleich zu
 *  `NUMMER_WIRD_VERGEBEN` (backend/src/jtl/schreiben/anlegen.ts) und zur Anlagemaske. */
const NUMMER_WIRD_VERGEBEN = "Nummer wird vergeben";

// Ursachen, die zum Wiederholen raten (`URSACHEN_MIT_WIEDERHOLUNGSRAT`), stehen in `lib/ausgangUnklar.ts`: In den
// Meldungen der Anlage ist ein Wiederholungsrat gefaehrlich (die Doppelanlage vom 2026-08-15 hat drei Bestellungen in
// JTL erzeugt), deshalb steht in der Warnung von Schritt 2 keiner dieser Saetze (R-T6a); die Warnung "Bitte NICHT
// erneut speichern ..." ist dort immer der letzte Satz.

// Ursachen, nach denen bei Schritt 1 (`POST /api/jtl/orders`) UNGEWISS ist, ob JTL die Bestellung trotzdem angelegt hat,
// stehen in `lib/ausgangUnklar.ts` (R-T6b; dieselbe Menge nutzt die Teillieferung, R-T7d).

const AUSGANG_UNKLAR_MELDUNG =
  "Ob die Bestellung in JTL angelegt wurde, ist unklar – bitte in JTL prüfen, bevor erneut gespeichert wird.";

/**
 * Der stehende Hinweis zu einem Bestellexcel-Beleg, der zu gross war oder dessen Upload scheiterte (Task 7 "Uploads
 * bis 100 MB"): `Bestellexcel-Beleg für BE-10440: <Grund> Bitte im Reiter „Dokumente“ nachtragen.` Die Anfuehrungszeichen
 * sind typografisch (U+201E, U+201C); `grund` ist ein ganzer Satz (`dateiZuGrossText` bzw. `uploadFehlerText`).
 * `bezug` ist die Bestellnummer, ohne vergebene Nummer `JTL-Nummer <k>`.
 */
function belegHinweisText(bezug: string, grund: string): string {
  return `Bestellexcel-Beleg für ${bezug}: ${grund} Bitte im Reiter „Dokumente“ nachtragen.`;
}

/** Der synchrone Riegel schliesst das Fenster zwischen zwei Ereignissen vor dem React-Re-Render. */
export function anlageStarten(riegel: { current: boolean }): boolean {
  if (riegel.current) return false;
  riegel.current = true;
  return true;
}

/**
 * EINE Quelle fuer die Feldliste der Erfolgsmeldung aus `neueBestellungAnlegen` unten (Fix-Runde 2
 * zu Task 9, Major 1) - vorher tippten Kopfkommentar, Toast UND `test/reference/bundle.test.ts` die
 * Liste je EIGENSTAENDIG ab, drei Kopien, die sich widersprachen; der Toast liess dabei
 * `estimatedDeliveryBuchen` weg. Exportiert, DAMIT `test/komponenten/App/verdrahtungAnlage.test.tsx`
 * gegen dieselbe Quelle pruefen kann statt gegen eine erneut abgetippte Zeichenkette - eine
 * unvollstaendige Liste hier faellt dadurch im Test auf, nicht erst beim naechsten Abschreibfehler in
 * einer vierten Kopie. `billingAddress` -> "Rechnungsadresse" steht EXAKT wie eine sichtbare
 * Beschriftung in `Anlagemaske.tsx`.
 *
 * SEIT DEM 2026-08-15 KURZ, und das ist die eigentliche Nachricht dieser Aenderung: die Maske legt
 * jetzt direkt in JTL an (`neueBestellungAnlegen`), und dorthin gehen Lager (als `lagerId`),
 * "vsl. LT 74722 Buchen" (als `lieferdatum`), Rechnungsnummer (als `fremdbelegnummer`) und die
 * Positionen mit. Von den sechs bis dahin verworfenen Feldern (Lager, Liefertermin, vsl. LT 74722
 * Buchen, Rechnungsnummer, Zahlungsplan, Positionen) blieb zunaechst Liefertermin neben Zahlungsplan
 * stehen, fuer die JTLs `spLieferantenBestellungErstellen` keine Entsprechung hatte. Rechnungsadresse
 * kam als dritter Eintrag im Register unten dazu, obwohl sie keiner der sechs war - erst mit
 * `767b467` (2026-08-15).
 *
 * LIEFERTERMIN GEHT SEIT DEM 2026-08-26 MIT (Auftrag "Liefertermin auf echtes JTL-Feld umstellen"):
 * `cDruckAnmerkung`, ein Freitextfeld unterhalb der Artikelpositionen im JTL-Beschaffungsfenster, war
 * bereits Parameter BEIDER Schreibprozeduren - nur bis dahin nirgends gelesen oder geschrieben. Der
 * Eingabewert (`deliveryTermText`) geht seither als `deliveryTermText` mit in `POST /api/jtl/orders`
 * (`neueBestellungAnlegen` unten), genau wie `lagerId`. "Liefertermin" ist damit aus dieser Liste
 * ENTFALLEN - der Auftrag stellt das Feld komplett auf JTL um (Anlegen, Anzeigen, Sperren nach der
 * Anlage), genau wie Lieferant und Lager. Die Rechnungsadresse bleibt eine ABSINA-Konstante, die JTL
 * aus dem eigenen Stamm zieht.
 */
export const ANLAGEMASKE_VERWORFENE_FELDER = [
  "Rechnungsadresse",
] as const;

/**
 * Deutsche Aufzaehlung ("A, B und C") - reines Textwerkzeug ausschliesslich fuer die Erfolgsmeldung
 * unten, deshalb hier und nicht in `lib/`.
 */
function alsAufzaehlung(teile: readonly string[]): string {
  if (teile.length <= 1) return teile.join("");
  return `${teile.slice(0, -1).join(", ")} und ${teile[teile.length - 1]}`;
}

/**
 * Formt die Positionen der Maske in die Form, die `POST /api/jtl/orders` verlangt - und WIRFT bei
 * jeder Luecke, statt sie zu fuellen.
 *
 * Das ist die tragende Entscheidung dieser Funktion: JTL bucht einen produktiven Beleg. Eine
 * fehlende Artikelnummer mit 0 zu ersetzen liefe in die Freipositions-Ablehnung des Backends,
 * ein fehlender Steuersatz als 0 % waere eine stille Falschangabe auf einer echten Bestellung.
 * Beides muss der Nutzer sehen, bevor irgendetwas geschrieben wird.
 *
 * Die Luecken werfen `NutzerFehler` (Fehleranzeige Paket 1): `fehlertext` gibt deren Satz unveraendert aus,
 * ein gewoehnlicher `Error` ergaebe nur den Sammeltext "Unerwarteter Fehler im Browser".
 */
function jtlPositionenAus(items: readonly OrderItem[]): JtlNeuePosition[] {
  const brauchbar = items.filter((item) => String(item.sku ?? "").trim() !== "" || item.kArtikel != null);
  if (brauchbar.length === 0) {
    throw new NutzerFehler("Die Bestellung hat keine Positionen - JTL nimmt keine leere Bestellung an.");
  }
  return brauchbar.map((item, index) => {
    const kArtikel = Number(item.kArtikel);
    if (!Number.isInteger(kArtikel) || kArtikel < 1) {
      throw new NutzerFehler(
        `Position ${index + 1} („${String(item.name ?? item.sku ?? "")}") stammt nicht aus der ` +
          "JTL-Artikelsuche - JTL nimmt keine freien Positionen an.",
      );
    }
    const fMenge = Number(item.qty);
    if (!Number.isFinite(fMenge) || fMenge <= 0) {
      throw new NutzerFehler(`Position ${index + 1} hat keine gültige Menge.`);
    }
    const fEKNetto = Number(item.price);
    if (!Number.isFinite(fEKNetto)) {
      throw new NutzerFehler(`Position ${index + 1} hat keinen gültigen EK-Preis.`);
    }
    const fMwSt = Number(item.mwstProzent);
    if (!Number.isFinite(fMwSt)) {
      throw new NutzerFehler(
        `Für Position ${index + 1} ist in JTL kein Steuersatz hinterlegt (tliefartikel.fMwSt).`,
      );
    }
    return {
      kArtikel,
      cArtNr: String(item.sku ?? ""),
      fMenge,
      fEKNetto,
      fMwSt,
      dLieferdatum: null,
    };
  });
}

/**
 * Neuanlage schreibt zuerst JTL, danach Hub und zuletzt nachrangige Zusatzdaten.
 *
 * `zeigeHinweis` (Task 7 "Uploads bis 100 MB", wie `zeigeMeldung` ein Parameter am Ende): der STEHENDE Hinweis der App
 * (`StehendeHinweise.tsx`). Er meldet allein den Bestellexcel-Beleg (Schritt 4), wenn er zu gross ist oder sein Upload
 * scheitert; die Erfolgsmeldung bleibt ein Toast ohne Nachsatz dazu.
 */
export function useBestellungAnlegen(
  jtlLager: readonly JtlLager[],
  versende: Dispatch<Aktion>,
  setNeueBestellungOffen: Dispatch<SetStateAction<boolean>>,
  zeigeMeldung: (text: string) => void,
  zeigeHinweis: (text: string) => void,
) {
  const [neueBestellungLaeuft, setNeueBestellungLaeuft] = useState(false);
  const neueBestellungLaeuftRef = useRef(false);

  /**
   * `AnlagemaskeProps.onSpeichern` (`Anlagemaske.tsx#AnlagemaskeProps`) - Behaelterarbeit, wie ihr
   * eigener Kopfkommentar sagt ("Anlegen/Speichern ueber die API ist Behaelterarbeit, nicht Teil
   * dieser reinen Darstellungskomponente"). Verdrahtet auf
   * `neueBestellungAnlegen` unten, das seit dem 2026-08-15 im Erfolgspfad VIER Aufrufe absetzt
   * (SCHRITT 1-3 dort): `POST /api/jtl/orders`, danach `bestellungAnlegen` (`api/bestellungen.ts`)
   * auf `POST /api/orders`, danach `jtlBestellungLaden` auf `GET /api/jtl/orders/:k` und zuletzt
   * `positionszeilenAnlegen` auf `POST /api/orders/:id/items` fuer die Positionszeilen. Seit Task 3/4
   * (Plan 2026-08-17-bestellexcel-anlagemaske, SCHRITT 4/4b weiter unten) kommen BEDINGT bis zu ZWEI
   * weitere hinzu, wenn die Anlagemaske eine Bestellexcel-Datei durchgereicht hat: der Beleg-Upload
   * (`dokumentHochladen`) und, sofern dabei auch ein Entwurf vorliegt, der Schreibvorgang auf
   * `orderExcelImport` (`bestellungAendern`) - macht SECHS Aufrufe im vollen Erfolgspfad mit
   * Bestellexcel-Import, VIER ohne.
   *
   * `kLieferant` bekommt in den ERSTEN BEIDEN der vier Aufrufe (SCHRITT 1 und 2) dieselbe Bruecke -
   * SCHRITT 3 (`GET .../:k` und `POST .../items`) schickt ihn nicht mit: `supplierId` ist die JTL-
   * Lieferantennummer als Text (`String(kLieferant)`, siehe `anlagemaskeLieferantenSuche` oben) - in
   * `neueBestellungAnlegen` nur `Number(daten.supplierId)` zurueckgewandelt, keine Annahme, sondern
   * dieselbe Kennung, die die Suche gerade erst geliefert hat.
   *
   * STAND HIER BIS ZUM 2026-08-15 - GEMESSEN, NICHT ANGENOMMEN (Task-Brief: "miss, welche Felder der
   * Endpunkt wirklich annimmt"): `POST /api/orders` war der EINZIGE Aufruf, den diese Funktion
   * absetzte. `bestellungAnlegenAusKoerper` (`backend/src/http/routes/orders.ts#bestellungAnlegenAusKoerper`)
   * las aus dem Koerper AUSSCHLIESSLICH `kLieferant`/`name`/`orderDate`/`etaBookingOffsetDays` -
   * keiner dieser vier Namen kam unter demselben Namen in `AnlagemaskeDaten` vor. `legeBestellungAn`
   * (`backend/src/domaene/orders.ts`) schrieb entsprechend NUR `k_lieferant` in `orders` plus
   * `name`/`order_date`/`eta_booking_offset_days` in `order_extras` - eine neu angelegte Bestellung
   * trug bis auf den Lieferanten NICHTS von dem, was die Maske erfasst hatte: kein Lager, kein
   * Liefertermin, keine Rechnungsnummer, kein Zahlungsplan, keine Position. Der Server verwarf diese
   * Felder dabei NICHT aktiv (anders als `bestellungAendern`/RIEGEL 2: `bestellungAnlegenAusKoerper`
   * destrukturierte nur die vier bekannten Namen und ignorierte den Rest kommentarlos) - er nahm sie
   * nur nie auf. Eine BENANNTE LUECKE IM BACKEND, kein Reparaturauftrag der damaligen Runde:
   * verdrahtet wurde, was verdrahtbar war (`kLieferant`), der Rest wurde laut benannt statt still
   * verschluckt.
   *
   * Genau diese Luecke ist mit dem 2026-08-15 geschlossen worden (`neueBestellungAnlegen` legt seither
   * zuerst per `POST /api/jtl/orders` in JTL an): zunaechst kamen SECHS der zehn `AnlagemaskeDaten`-
   * Felder an, VIER nicht - darunter `deliveryTermText` ("Liefertermin"), fuer das JTL an dieser
   * Stelle nur ein echtes Datum kannte, keinen vom Nutzer getippten Freitext.
   *
   * SEIT DEM 2026-08-26 (Auftrag "Liefertermin auf echtes JTL-Feld umstellen") kommen SIEBEN an, NUR
   * NOCH DREI nicht: `deliveryTermText` geht seither als `deliveryTermText` mit (`cDruckAnmerkung`,
   * ein Freitextfeld unterhalb der Artikelpositionen im JTL-Beschaffungsfenster - bereits Parameter
   * beider Schreibprozeduren, nur bis dahin nirgends gelesen oder geschrieben). Warum die
   * verbleibenden drei im Register `ANLAGEMASKE_VERWORFENE_FELDER` oben stehen (zwei davon) bzw.
   * `supplierName` nicht, hat weiterhin KEIN gemeinsames Merkmal - jedes hat einen eigenen Grund:
   * `billingAddress` ("Rechnungsadresse") hat kein
   * editierbares Feld, aber einen eigenen beschrifteten Block ("Fest hinterlegt", `Anlagemaske.tsx`)
   * - JTL zieht dieselbe Adresse zwar aus dem eigenen Stamm, die Meldung nennt sie trotzdem, weil sie
   * im Formular sichtbar als Teil der Bestellung dasteht. `supplierName` hat dagegen KEIN eigenes
   * UI-Element (nur `lieferant.name` als Nebenprodukt der "Lieferant"-Suche) und ist redundant zu
   * `kLieferant`/`supplierId`, das ueber die Bruecke ohnehin ankommt - deshalb nicht im Register.
   *
   * Original (`saveNewOrder`, index.html:8144-8151) oeffnet nach dem Anlegen zusaetzlich den Drawer
   * der neuen Bestellung und wechselt auf das Board. NICHT nachgebaut: `zustand.bestellungen` ist im
   * Moment DIESES Aufrufs noch der ALTE Stand (`versende` unten wirkt erst beim naechsten Rendern) -
   * ein sofortiges `drawerOeffnen(id)` faende die Bestellung nicht und bliebe wirkungslos (Guard `if
   * (!bestellung) return`, siehe dort). Ein BENANNTER Verhaltensunterschied, kein Versehen.
   *
   * FIX-RUNDE 1 (Major 1) - DIE MELDUNG LOG BISHER UEBER DIE EIGENE LUECKE: bis zum 2026-08-11 stand
   * im Erfolgszweig unbedingt `zeigeMeldung("Bestellung angelegt")` - wortgleich fuer den oben
   * gemessenen Fall, in dem damals SECHS Maskenfelder (Lager, Liefertermin, vsl. LT 74722 Buchen,
   * Rechnungsnummer, Zahlungsplan, Positionen) NICHT gespeichert wurden. Der Nutzer bekam damit die
   * ausdrueckliche Bestaetigung, dass alles geklappt hat, waehrend nur der Lieferant ankam - "lautlos
   * verfallen" ist genau die Flaeche, gegen die dieses Projekt antritt (siehe
   * `test/reference/bundle.test.ts`, Abschnitt "SICHTBARE Bedienelemente ohne Wirkung"). Die Meldung
   * benennt seit dem 2026-08-11, was verworfen wurde, statt einen uneingeschraenkten Erfolg zu
   * behaupten.
   *
   * FIX-RUNDE 2 (Major 1), SELBER TAG - DIE MELDUNG SELBST NANNTE NUR FUENF DER SECHS FELDER: der
   * Fix-Runde-1-Toast liess `estimatedDeliveryBuchen`/"vsl. LT 74722 Buchen" aus, WAEHREND dieser
   * Kopfkommentar schon alle sechs nannte - UND `test/reference/bundle.test.ts` tippte eine dritte,
   * wieder andere Liste ab (ohne "Zahlungsplan"). Drei handgetippte Kopien, die sich widersprachen,
   * statt eines Tippfehlers. `ANLAGEMASKE_VERWORFENE_FELDER` oben ist seither die EINE Quelle - damals
   * mit sechs Eintraegen, seit dem 2026-08-15 mit zwei der sechs plus Rechnungsadresse als neuem
   * dritten Eintrag (siehe oben): der Toast unten
   * liest sie ueber `alsAufzaehlung`, dieser Kommentar verweist nur noch darauf statt die Namen ein
   * zweites Mal abzutippen.
   *
   * FIX-RUNDE 1 (Major 1, Punkt 3), NACHGEFUEHRT (Zweig Board-Dokumentstand) - WAS DIE ANTWORT VON
   * `POST /api/orders` TRAEGT: `bestellungAnlegen` (`api/bestellungen.ts`) laeuft wie `bestellungLaden`
   * durch `entschachtele()`. Die Antwort (`backend/src/http/routes/orders.ts`, `router.post("/")`) traegt den
   * Bestellkopf, `extras` (hier flach gemacht, samt `extrasRowVersion`), `payments` (die Raten eines
   * uebernommenen Lieferantenstandards) und `dokumentStand` (alle Pflichttypen offen; ohne das Feld zaehlte
   * die Uebersicht die neue Bestellung bis zum Neuladen als 0/0). Der ueber `versende` unten in den Store
   * gelegte Datensatz ist also kein blanker `orders`-Kopf mehr. Erst ein Nachladen (`GET /api/orders/:id`
   * samt JTL-Anreicherung) liefert, was die Anlage nicht schreibt oder JTL fuehrt: `orderNo` und `supplier`,
   * `tasks`, `pdfs` und `items` (diese setzt hier nur Schritt 3 unten, falls Positionszeilen entstehen).
   * Bis dahin zeigt das Board fuer die neue Zeile nur "Ohne Bestellnummer" (`Bestellzeile.tsx`, Eigenheit 5).
   */
  const neueBestellungAnlegen = useCallback((daten: AnlagemaskeDaten): void => {
    const kLieferant = Number(daten.supplierId);
    if (!Number.isInteger(kLieferant) || kLieferant < 1) {
      meldeFehler("Ohne einen aus der JTL-Suche gewählten Lieferanten kann nicht angelegt werden.");
      return;
    }
    const lagerId = daten.duplikatLagerId ?? lagerKennung(daten.warehouse, jtlLager);
    if (typeof lagerId !== "number" || !Number.isInteger(lagerId) || lagerId < 1) {
      meldeFehler(`Für das Lager „${daten.warehouse}" ist kein JTL-Warenlager hinterlegt.`);
      return;
    }
    if (!daten.estimatedDeliveryBuchen) {
      meldeFehler("Ohne „vsl. LT 74722 Buchen\" fehlt JTL das Lieferdatum.");
      return;
    }

    let positionen: JtlNeuePosition[];
    try {
      positionen = jtlPositionenAus(daten.items);
    } catch (fehler) {
      // Der Satz der Luecke kommt unveraendert (`NutzerFehler`), ohne vorangestellten Kontext.
      meldeFehler(fehler);
      return;
    }

    if (!anlageStarten(neueBestellungLaeuftRef)) return;
    setNeueBestellungLaeuft(true);

    // SCHRITT 1: JTL. Zuerst, nicht danach - JTL vergibt die Bestellnummer, und die Tool-Zeile soll
    // sie von Anfang an tragen. Scheitert dieser Aufruf, entsteht gar keine Bestellung; die
    // umgekehrte Reihenfolge hinterliesse bei einem Fehlschlag eine Tool-Bestellung ohne
    // Entsprechung in JTL - genau der Zustand, in dem die zwei Bestellungen vom 2026-08-14 endeten.
    anfrage<{ kLieferantenBestellung: number; eigeneBestellnummer: string | null }>(
      "/api/jtl/orders",
      {
        methode: "POST",
        koerper: {
          kLieferant,
          lagerId,
          lieferdatum: daten.estimatedDeliveryBuchen,
          fremdbelegnummer: daten.invoiceNo.trim() || null,
          waehrung: daten.eur ? "EUR" : "USD",
          ansprechpartner: null,
          emailEmpfaenger: null,
          // Auftrag "Liefertermin auf echtes JTL-Feld umstellen" (2026-08-26): geht jetzt mit, wie
          // Lager auch - der Eingabewert existierte bereits (siehe `Anlagemaske.tsx#liefertermin`),
          // wurde bisher aber verworfen (`ANLAGEMASKE_VERWORFENE_FELDER`, siehe Kopfkommentar dort).
          deliveryTermText: daten.deliveryTermText.trim() || null,
          positionen,
        },
      },
    )
      // SCHRITT 2: die Tool-Buchfuehrung, MIT der eben vergebenen JTL-Nummer. Ohne sie faende
      // `orderIdZuJtlKennung` (jtlSchreiben.ts) die Bestellung spaeter nicht, und weder Split noch
      // Teillieferung liesse sich auf ihr nicht ausfuehren.
      .then((jtlBestellung) =>
        bestellungAnlegen({
          kLieferant,
          jtlOrderId: jtlBestellung.kLieferantenBestellung,
          name: jtlBestellung.eigeneBestellnummer,
        } as unknown as Partial<Order>)
          .then((neu) => ({ neu, jtlBestellung }))
          .catch((fehler: unknown) => {
            /**
             * AB HIER STEHT DIE BESTELLUNG IN JTL - der Fehlschlag betrifft nur die
             * Tool-Buchfuehrung. Diese Unterscheidung ist am 2026-08-15 teuer gelernt worden:
             * scheiterte Schritt 2, blieb die Maske offen und zeigte eine Meldung, die nach einem
             * gewoehnlichen Fehler aussah. Drei Klicks spaeter standen DREI Bestellungen in JTL
             * (2180/2181/2182, alle mit derselben Fremdbelegnummer) und keine einzige im Hub.
             *
             * Deshalb beides: die Maske schliesst (ein zweiter Klick kann gar nicht mehr passieren),
             * und die Meldung nennt die JTL-Nummer samt ausdruecklicher Warnung. Der Hub-Eintrag
             * laesst sich nachtragen; eine zweite Bestellung in der produktiven Warenwirtschaft
             * nicht ohne Weiteres.
             */
            setNeueBestellungOffen(false);
            // Die Warnung ist der LETZTE Satz (R-T6a). Eine Ursache steht davor, aber nur eine, die nicht zum
            // Wiederholen raet: die Sammelsaetze des Uebersetzers ("Bitte gleich noch einmal versuchen" u. a.)
            // entfallen ganz, denn sie heben die Warnung auf - die Bestellung steht in JTL. Spezifische
            // Ursachen (Servertext, Berechtigung ...) bleiben. Ein 401 hat keinen Ursachetext; die Warnung
            // kommt trotzdem.
            const grund = fehlertext(fehler);
            const ursache = grund === null || URSACHEN_MIT_WIEDERHOLUNGSRAT.includes(grund) ? "" : ` ${grund}`;
            throw new NutzerFehler(
              `Die Bestellung steht bereits in JTL (Nummer ${jtlBestellung.kLieferantenBestellung}), ` +
                `konnte im Hub aber nicht erfasst werden.${ursache} Bitte NICHT erneut speichern, ` +
                "das legte eine zweite Bestellung in JTL an.",
            );
          }),
      )
      /**
       * SCHRITT 3 (2026-08-15): die Positionszeilen des Tools.
       *
       * `order_item_extras` entstand bis hierher AUSSCHLIESSLICH in der Migration - im laufenden
       * Betrieb legte niemand je eine Zeile an. `schreibeItemExtras` ist ein reines `UPDATE`; jede
       * ueber diese Maske angelegte Bestellung hatte damit keine Zeile, die ein spaeterer
       * `PATCH .../items/:jtlPosId` haette treffen koennen. Sichtbar wurde das als M-Kaestchen, das
       * sich anklicken liess und nach dem Nachladen zurueckstand - und dasselbe galt fuer FOC und
       * saemtliche Kostenfelder der EK-Tabelle.
       *
       * Er steht NACH Schritt 2 und nicht davor: der Schluessel der Zeilen ist
       * `(order_id, jtl_pos_id)`, und beide Haelften gibt es erst jetzt - die `order_id` aus der
       * Tool-Buchfuehrung, die `kLieferantenBestellungPos` aus JTL.
       *
       * SEIN FEHLSCHLAG BRICHT DEN VORGANG NICHT AB. Die Bestellung steht dann in JTL UND im Hub;
       * was fehlt, sind die Zusatzfelder, und die lassen sich nachtragen. Ein Wurf hier saehe aus
       * wie ein gescheitertes Anlegen und lockte zu genau dem zweiten Klick, den der Fehlerpfad in
       * Schritt 2 mit drei JTL-Bestellungen bezahlt hat. Deshalb: Meldung statt Abbruch.
       */
      .then(async ({ neu, jtlBestellung }) => {
        const id = String((neu as { id?: unknown }).id ?? "");
        let datensatz: Order = neu;
        let nachsatz = "";
        // Die Bestellnummer, wie die Erfolgsmeldung unten sie nennt. Der Beleg meldet sich ueber einen eigenen,
        // stehenden Hinweis (`belegHinweis`, nach der Erfolgsmeldung) und nicht ueber `nachsatz`.
        const bestellNummer = jtlBestellung.eigeneBestellnummer ?? NUMMER_WIRD_VERGEBEN;
        // Der Bezug des Hinweises: hat JTL keine Nummer vergeben (`eigeneBestellnummer: null`, siehe
        // `backend/src/jtl/schreiben/anlegen.ts`), waere "Bestellexcel-Beleg für Nummer wird vergeben" kein Bezug -
        // dann steht die JTL-Nummer da. Die Erfolgsmeldung unten bleibt bei `bestellNummer`.
        const belegBezug = jtlBestellung.eigeneBestellnummer ?? `JTL-Nummer ${jtlBestellung.kLieferantenBestellung}`;
        let belegHinweis: string | null = null;
        try {
          const jtl = await jtlBestellungLaden(jtlBestellung.kLieferantenBestellung);
          const { zeilen, kennzeichenUebernommen } = positionszeilenAus(jtl.positionen, daten.items);
          if (zeilen.length > 0) {
            // Die Antwort traegt alle Zeilen samt `rowVersion` - genau das, was der Drawer braucht,
            // um danach ueberhaupt patchen zu koennen. Sie hier mitzugeben spart das Nachladen.
            const items = await positionszeilenAnlegen(id, zeilen);
            datensatz = { ...(neu as Record<string, unknown>), items };
            if (!kennzeichenUebernommen) {
              nachsatz =
                " Die Kennzeichen M und FOC ließen sich den JTL-Positionen nicht zweifelsfrei" +
                " zuordnen und stehen noch nicht - bitte im Reiter „Bestellung\" nachsetzen.";
            }
          }
        } catch (fehler) {
          console.error(`Positionszeilen der Bestellung "${id}" konnten nicht angelegt werden:`, fehler);
          nachsatz =
            " Die Positionszeilen konnten nicht angelegt werden; M, FOC und die Kostenfelder sind" +
            " an dieser Bestellung noch nicht speicherbar.";
        }

        /**
         * SCHRITT 4 (Task 3, Plan 2026-08-17-bestellexcel-anlagemaske): der Bestellexcel-Beleg. NUR
         * wenn die Anlagemaske eine Datei durchgereicht hat (`PositionsEditor.tsx#onExcelDatei`) - der
         * Belegweg ist PRO BESTELLUNG (`dokumentHochladen(orderId, "bestellexcel", datei)`,
         * `api/dokumente.ts:32`), die Bestellung muss also existieren; deshalb erst HIER, nach
         * Schritt 2, mit der eben angelegten `id`.
         *
         * NACHRANGIG WIE SCHRITT 3 DIREKT DARUEBER, aus demselben am 2026-08-15 teuer gelernten Grund
         * (siehe Kommentarblock zu Schritt 2 oben): die Positionen stehen zu diesem Zeitpunkt laengst
         * in JTL (Schritt 1) UND im Hub (Schritt 2). Fehlt nur der abgelegte Beleg, darf das nicht wie
         * ein gescheitertes Anlegen aussehen - sonst klickt der Nutzer erneut und JTL bekommt eine
         * zweite Bestellung fuer dieselbe Sache. Deshalb: kein Wurf.
         *
         * Task 7 ("Uploads bis 100 MB"): der Beleg hat keinen Nachsatz in der Erfolgsmeldung mehr. Ist er zu gross
         * oder scheitert sein Upload, meldet ihn ein STEHENDER Hinweis (`zeigeHinweis`) mit Bestellnummer, Grund
         * (`dateiZuGrossText` bzw. `uploadFehlerText`) und der Bitte, ihn im Reiter „Dokumente“ nachzutragen - eine
         * Erfolgsmeldung von 2,2 Sekunden liesse einen Upload, der nach Minuten scheitert, unbemerkt.
         */
        if (daten.excelDatei) {
          /**
           * NACHZUG (F2, Fixwelle nach der Endabnahme): Global Constraint 17, wie an den drei
           * anderen Uploadwegen (`App.tsx:1214`, `App.tsx:2494`, `DokumenteTab.tsx:209`) - bis
           * hierher fehlte der Vorabcheck genau hier. Ohne ihn wurde eine zu grosse Excel bereits
           * LOKAL ausgewertet (`PositionsEditor.tsx#excelDateiVerarbeiten` prueft die Groesse
           * nicht) und die Positionen ersetzt, WAEHREND die Bestellung in JTL und im Hub (Schritt
           * 1/2 oben) laengst ohne Beleg entstand - der Fehlschlag waere erst hier, am Server
           * (413), sichtbar geworden und nur als Nachsatz. Der Check hier verhindert wenigstens
           * den serverseitigen Fehlversuch und macht den Ausfall sofort erklaerbar.
           */
          if (istZuGross(daten.excelDatei)) {
            belegHinweis = belegHinweisText(belegBezug, dateiZuGrossText(daten.excelDatei));
          } else {
            try {
              await dokumentHochladen(id, "bestellexcel", daten.excelDatei);

              /**
               * SCHRITT 4b (Task 4, Plan 2026-08-17-bestellexcel-anlagemaske): der Auswertungssatz
               * selbst. `orderExcelImport` liegt seit diesem Task in der Feldgruppe `extras`
               * (`backend/src/domaene/feldgruppen.ts`) und ist damit ueber `bestellungAendern`
               * schreibbar - NUR wenn Task 3 auch einen Entwurf durchgereicht hat
               * (`PositionsEditor.tsx#onExcelDatei` liefert Datei UND Entwurf zusammen; `excelEntwurf`
               * fehlt nur, wenn `excelDatei` es aus irgendeinem Grund nicht tut).
               *
               * FORM wie `index.html:8128-8132`, woertlich: der Entwurf (`sourceName`/`sheetName`/
               * `headers`/`rowCount`/`warnings`/`extractedAt` aus `PositionsEditor.tsx#excelDateiVerarbeiten`),
               * ueberschrieben mit den TATSAECHLICH uebernommenen Positionen (`daten.items` - nach der
               * JTL-Artikelbestaetigung, nicht die rohen Excel-Zeilen) und `appliedAt`.
               *
               * Die Extras-Version stammt aus der frischen Anlageantwort. Wendet diese innerhalb
               * derselben Transaktion einen Lieferantenstandard an, ist sie bereits hoeher als 1;
               * ein Literal wuerde den folgenden PATCH dann sicher mit 409 abweisen.
               *
               * NACHRANGIG WIE DER BELEG-UPLOAD DARUEBER: scheitert nur dieser Schreibvorgang, gilt die
               * Bestellung TROTZDEM als angelegt (Beleg UND Positionen stehen laengst) - kein Wurf, nur
               * ein weiterer Nachsatz.
               */
              if (daten.excelEntwurf) {
                try {
                  const geaendert = await bestellungAendern(
                    id,
                    {
                      orderExcelImport: {
                        ...daten.excelEntwurf,
                        items: daten.items.map((item) => ({
                          name: item.name,
                          sku: item.sku,
                          ean: item.ean || "",
                          qty: item.qty,
                          price: item.price,
                        })),
                        appliedAt: new Date().toISOString(),
                      },
                    },
                    Number(datensatz.extrasRowVersion) || 0,
                  );
                  datensatz = { ...datensatz, ...geaendert };
                } catch (fehler) {
                  if (fehler instanceof NachladenNachSchreibenFehler) {
                    datensatz = {
                      ...datensatz,
                      ...(fehler.extrasRowVersion === undefined
                        ? {}
                        : { extrasRowVersion: fehler.extrasRowVersion }),
                    };
                  }
                  console.error(
                    `Bestellexcel-Auswertung der Bestellung "${id}" konnte nicht abgelegt werden:`,
                    fehler,
                  );
                  nachsatz +=
                    " Die Bestellexcel-Auswertung konnte nicht abgelegt werden; bitte im Reiter" +
                    " „Dokumente“ prüfen.";
                }
              }
            } catch (fehler) {
              console.error(`Bestellexcel-Beleg der Bestellung "${id}" konnte nicht abgelegt werden:`, fehler);
              belegHinweis = belegHinweisText(belegBezug, uploadFehlerText(fehler, daten.excelDatei));
            }
          }
        }

        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz });
        setNeueBestellungOffen(false);
        const meldung =
          `Bestellung in JTL angelegt: ${bestellNummer}` +
          ` (JTL-Nummer ${jtlBestellung.kLieferantenBestellung}) - ` +
          `${alsAufzaehlung(ANLAGEMASKE_VERWORFENE_FELDER)} kennt JTL an dieser Stelle nicht.` +
          nachsatz;
        // Der Dauerzusatz ("Rechnungsadresse kennt JTL nicht") steht bei JEDER Anlage und gehoert in den
        // Toast; ein Nachsatz heisst, dass etwas NICHT abgelegt wurde (Teilerfolg) und bleibt im Stapel stehen.
        if (nachsatz === "") zeigeMeldung(meldung);
        else meldeFehler(meldung);
        // Der Beleg-Upload (Bestellexcel) meldet NUR ueber den stehenden Hinweis, nicht zusaetzlich im Stapel.
        if (belegHinweis !== null) zeigeHinweis(belegHinweis);
      })
      // FIX-RUNDE 2 (Minor 2, in Runde 1 vertagt): bis hierher stand unbedingt die generische
      // Sammelmeldung, WAEHREND `teillieferungAnlegen` unten `fehler.message` schon durchreichte -
      // man kann die Erfolgsmeldung nicht ehrlich machen und die Fehlermeldung stumm lassen.
      .catch((fehler: unknown) => {
        console.error("Bestellung konnte nicht angelegt werden:", fehler);
        // Ein `NutzerFehler` ist der Satz aus Schritt 2 ("steht bereits in JTL"): ein vorangestelltes "konnte
        // nicht angelegt werden" waere dort falsch, die Bestellung IST angelegt.
        if (fehler instanceof NutzerFehler) {
          meldeFehler(fehler);
          return;
        }
        // Jeder andere Fehler ist ein Fehlschlag von Schritt 1 (oder ein Programmfehler). `null` heisst
        // Abbruch oder 401: still, wie bei `meldeFehler`.
        const grund = fehlertext(fehler);
        if (grund === null) return;
        // Ungewisser Ausgang (R-T6b): die Antwort ging verloren, JTL kann die Bestellung trotzdem angelegt haben.
        // Ein "konnte nicht angelegt werden" samt "Bitte gleich noch einmal versuchen" laedt zur Doppelanlage ein
        // (2026-08-15); die Meldung sagt stattdessen, dass der Ausgang unklar ist. Die Maske bleibt offen.
        // Dazu gehoert der mehrdeutige Schreibfehler des Backends (R-T6f, R-Z4): `JtlMehrdeutigeErstellungError` antwortet
        // mit 502 und einem eigenen Text in Umschrift und Entwicklerton ("Es ist NICHT bekannt, ob ..."). Auf dieser Route
        // kommt ein Backend-502 nur daher; ein Proxy-502 (HTML) landet ohnehin in `ausgangUnklar`. Der Nutzer bekommt in
        // beiden Faellen den eigenen Satz, nicht den Servertext.
        if (ausgangUnklar(grund) || (fehler instanceof ApiFehler && fehler.status === 502)) meldeFehler(AUSGANG_UNKLAR_MELDUNG);
        // Jeder uebrige Fall - eindeutige Ablehnung (400 mit Servertext usw.): der feste Satz als Kontext (R1), die
        // Ursache dahinter.
        else meldeFehler(`Die Bestellung konnte nicht angelegt werden. ${grund}`);
      })
      .finally(() => {
        neueBestellungLaeuftRef.current = false;
        setNeueBestellungLaeuft(false);
      });
  }, [jtlLager, versende, setNeueBestellungOffen, zeigeMeldung, zeigeHinweis]);

  return { anlegen: neueBestellungAnlegen, laeuft: neueBestellungLaeuft };
}
